import React, { useState, useMemo } from 'react';
import { X, Plus, Loader2 } from 'lucide-react';
import { assetService } from '../../services/assetService';

// =============================================================================
// Add asset — creates one row in the register.
//
// Two shapes come out of this dialog:
//   a MACHINE     site + area, no parent          e.g. FILL-01  Filler #1
//   a COMPONENT   a parent machine, no area       e.g. FILL-01-GBX  Gearbox
// A component inherits its parent's site and area (the database overwrites them
// on write), so those inputs disappear once a parent is chosen rather than
// letting someone set a value that will be silently replaced.
//
// The register is capped at one hop, so only top-level assets are offered as
// parents — you cannot hang a component off a component.
// =============================================================================

const AddAssetModal = ({
  siteCode,
  existingCodes = [],
  assets = [],
  areas = [],
  classes = [],
  onAdded,
  onClose,
}) => {
  const [code, setCode] = useState('');
  const [description, setDescription] = useState('');
  const [classCode, setClassCode] = useState('');
  const [areaCode, setAreaCode] = useState('');
  const [parentCode, setParentCode] = useState('');
  const [criticality, setCriticality] = useState('');
  const [replacementValue, setReplacementValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // One hop only: a component can never be a parent.
  const parentOptions = useMemo(
    () => assets.filter((a) => !a.is_component && (!siteCode || a.site_code === siteCode)),
    [assets, siteCode]
  );
  const siteAreas = useMemo(
    () => areas.filter((a) => !siteCode || a.site_code === siteCode),
    [areas, siteCode]
  );

  const isComponent = !!parentCode;
  const parent = parentOptions.find((a) => a.asset_code === parentCode) || null;

  const trimmed = code.trim().toUpperCase();
  const duplicate = existingCodes.some((c) => c.toUpperCase() === trimmed);
  const problem = !trimmed
    ? ''
    : duplicate
      ? 'An asset with this code already exists.'
      : (!isComponent && !siteCode)
        ? 'Select a site in the top bar first — a machine belongs to one.'
        : '';
  const canAdd = trimmed && description.trim() && !problem && !busy;

  const submit = async () => {
    if (!canAdd) return;
    setBusy(true);
    setError('');
    try {
      const res = await assetService.saveAssets({
        assets: [{
          asset_code: trimmed,
          description: description.trim(),
          // A component's site comes from its parent; the database enforces it.
          site_code: isComponent ? parent?.site_code : siteCode,
          area_code: isComponent ? null : (areaCode || null),
          parent_asset_code: parentCode || null,
          asset_class_code: classCode || null,
          criticality: criticality || null,
          replacement_value: replacementValue === '' ? null : replacementValue,
        }],
      });
      if (res?.success) onAdded(trimmed);
      else setError(res?.message || 'Could not add the asset.');
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not add the asset.');
    } finally {
      setBusy(false);
    }
  };

  const inputClass =
    'text-sm rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 '
    + 'text-slate-800 dark:text-slate-100 px-2.5 py-2 outline-none focus:ring-2 focus:ring-slate-400 disabled:opacity-50';

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div className="w-full max-w-md rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-2xl max-h-full overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/95 flex items-start justify-between gap-3">
          <div>
            <h3 className="m-0 text-sm font-bold text-slate-800 dark:text-slate-100">Add asset</h3>
            <p className="m-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">
              Something the site maintains — a machine, a vehicle, a building service.
            </p>
          </div>
          <button
            type="button" onClick={onClose} disabled={busy} aria-label="Close"
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Code
            <input
              type="text" className={`${inputClass} font-mono`} placeholder="e.g. FILL-01"
              value={code} disabled={busy} autoFocus
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Description
            <input
              type="text" className={inputClass} placeholder="e.g. Filler #1"
              value={description} disabled={busy}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Class — what kind of thing this is
            <select
              className={inputClass} value={classCode} disabled={busy}
              onChange={(e) => setClassCode(e.target.value)}
            >
              <option value="">Not set</option>
              {classes.map((c) => (
                <option key={c.code} value={c.code}>{c.code} · {c.description}</option>
              ))}
            </select>
            <span className="text-[11px] text-slate-400">
              Maintenance job plans are written against a class, so one plan covers every asset of that kind.
            </span>
          </label>

          <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
            Part of — leave blank for a machine, choose a machine to add a component
            <select
              className={inputClass} value={parentCode} disabled={busy}
              onChange={(e) => setParentCode(e.target.value)}
            >
              <option value="">Not a component</option>
              {parentOptions.map((a) => (
                <option key={a.asset_code} value={a.asset_code}>{a.asset_code} · {a.description}</option>
              ))}
            </select>
          </label>

          {isComponent ? (
            <p className="m-0 text-xs text-slate-400">
              Inherits site {parent?.site_code || '—'}
              {parent?.area_code ? ` · area ${parent.area_code}` : ''} from {parentCode}.
            </p>
          ) : (
            <label className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Area
              <select
                className={inputClass} value={areaCode} disabled={busy}
                onChange={(e) => setAreaCode(e.target.value)}
              >
                <option value="">Not set</option>
                {siteAreas.map((a) => (
                  <option key={`${a.site_code}-${a.code}`} value={a.code}>{a.code} · {a.description}</option>
                ))}
              </select>
            </label>
          )}

          <div className="flex gap-3">
            <label className="flex-1 flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Criticality
              <select
                className={inputClass} value={criticality} disabled={busy}
                onChange={(e) => setCriticality(e.target.value)}
              >
                <option value="">Not set</option>
                <option value="A">A — production stops</option>
                <option value="B">B — degraded</option>
                <option value="C">C — nuisance</option>
              </select>
            </label>
            <label className="flex-1 flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
              Replacement value
              <input
                type="number" min="0" className={inputClass} placeholder="0"
                value={replacementValue} disabled={busy}
                onChange={(e) => setReplacementValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              />
            </label>
          </div>

          <p className="m-0 min-h-[1rem] text-xs text-red-600 dark:text-red-400">{problem || error || ''}</p>
          <p className="m-0 text-xs text-slate-400">
            Cost centre, work centre and the rest can be set inline on the register afterwards.
          </p>

          <div className="mt-1 flex justify-end gap-2">
            <button
              type="button" onClick={onClose} disabled={busy}
              className="px-4 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button" onClick={submit} disabled={!canAdd}
              className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-md text-white transition-colors ${
                canAdd ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400 dark:bg-slate-600'
              }`}
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              {busy ? 'Adding…' : 'Add asset'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAssetModal;
